import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { Button, Row, Col, Form, Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { GridReadyEvent, ColDef } from "ag-grid-community";
import Select from "react-select";
import type { ActionMeta } from "react-select";
import ExternalFilter from "src/views/Docuclipper/ExternalFilter";
import { datefilterParamsYYYYMMDD } from "../Analyze/AnalyzeTransactionsTable";
import { createAlert } from "src/redux/reducers/Alerts";
import { convertFilterModel, convertSortModel } from "src/utils/filters";
import { ReduxState } from "src/redux";
import { assignJobsToTagLevel, getJobs } from "src/docuclipper/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { fetchTagLevels } from "src/redux/reducers/TagLevel";

interface ProjectOption {
  value: string; // tagId
  label: string;
  l1: string;
  l2: string;
}

interface GroupedOption {
  label: string;
  options: ProjectOption[];
}

export const AssignJobsPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const gridApi = useRef<any>(null);

  const { tagLevels } = useSelector((state: ReduxState) => state.TagLevel);

  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(
    null
  );
  const [touched, setTouched] = useState({
    project: false,
    jobs: false,
  });
  const [pagination, setPagination] = useState({
    totalCount: 0,
    pageSize: 100, // Default page size
  });
  useEffect(() => {
    dispatch(fetchTagLevels());
  }, []);
  // Validation states
  const isProjectValid = !!selectedProject?.value;
  const areJobsValid = selectedJobs.length > 0;
  const showProjectError = touched.project && !isProjectValid;
  const showJobsError = touched.jobs && !areJobsValid;

  // Convert tagLevels to grouped options for react-select
  const groupedOptions = useMemo<GroupedOption[]>(() => {
    if (!tagLevels?.root) return [];

    return Object.entries(tagLevels.root).map(([l1, customerData]) => ({
      label: l1,
      options: Object.entries(customerData)
        .filter(([key]) => key !== "metadata")
        .map(([l2, projectData]) => ({
          value: (projectData as any).metadata?.tagId || "",
          label: l2,
          l1,
          l2,
        })),
    }));
  }, [tagLevels]);

  const onAssign = async (tagLevelId, jobIds: string[]) => {
    // Mock implementation - replace with actual API call
    // return Promise.resolve();

    assignJobsToTagLevel(tagLevelId, jobIds)
      .then(() => {
        setSelectedJobs([]);
        setTouched((prev) => ({ ...prev, jobs: false }));
        if (gridApi.current) {
          gridApi.current.deselectAll();
          gridApi.current.purgeInfiniteCache();
        }

        dispatch(
          createAlert({
            message: "Jobs assigned successfully",
            type: "success",
            timeout: 3000,
          })
        );
      })
      .catch((err) => {
        dispatch(
          createAlert({
            message: err.message || "Error assigning jobs",
            timeout: 0,
            type: "error",
          })
        );
      });
  };

  const externalFilterColumns = useMemo(
    () => [
      {
        field: "name",
        label: "Job Name",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
        configurable: true,
      },
      // {
      //   field: "date",
      //   label: "Date",
      //   filter: true,
      //   filterType: "date",
      //   defaultFilterType: "equals",
      //   configurable: true,
      // },
      // {
      //   field: "type",
      //   label: "Type",
      //   filter: true,
      //   filterType: "text",
      //   defaultFilterType: "equals",
      //   configurable: true,
      // },
      // {
      //   field: "type",
      //   label: "Job Type",
      //   filter: true,
      //   filterType: "select",
      //   options: ["Bank", "Invoice", "Form", "Other"].map((jobType) => ({
      //     label: jobType!,
      //     value: jobType!,
      //   })),
      //   defaultFilterType: "equals",
      // },
    ],
    []
  );

  const getRowsData = useCallback(
    async (params) => {
      try {
        // Convert AG Grid filter model to API filter format
        const filterModel = params.filterModel || {};
        const filters: Record<string, any> = {};

        // Handle specific filters
        if (filterModel.name?.filter) {
          filters.name = filterModel.name.filter;
        }
        if (filterModel.type?.filter) {
          filters.type = filterModel.type.filter;
        }
        if (filterModel.date?.dateFrom || filterModel.date?.dateTo) {
          filters.dateFrom = filterModel.date?.dateFrom;
          filters.dateTo = filterModel.date?.dateTo;
        }

        const response = await getJobs({
          type: "ExtractData,Invoice,Form" as any,
          status: "Succeeded",
          all: false,
          top: "",
          paying: false,
          offset: params.startRow,
          limit: params.endRow - params.startRow,
          tags: "",
          unassigned: true,
          filters: [
            {
              field: "name",
              operator: "contains",
              value: filterModel.name?.filter || "",
            },
            {
              field: "type",
              operator: "equals",
              value: filterModel.type?.filter || "",
            },
            {
              field: "date",
              operator: "between",
              valueFrom: filterModel.date?.dateFrom,
              valueTo: filterModel.date?.dateTo,
            },
          ],
          sorting: [
            {
              field: "date",
              direction: "DESC",
            },
          ],
        });

        setPagination((prev) => ({
          ...prev,
          totalCount: response.count,
        }));

        params.successCallback(response.rows, response.count);
      } catch (err) {
        params.failCallback();
        dispatch(
          createAlert({
            message: "Error fetching jobs",
            timeout: 0,
            type: "error",
          })
        );
      }
    },
    [dispatch]
  );

  const dataSource = useMemo(
    () => ({
      rowCount: undefined,
      getRows: getRowsData,
    }),
    [getRowsData]
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      gridApi.current = params.api;
      gridApi.current.setDatasource(dataSource);
    },
    [dataSource]
  );

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        pinned: "left" as const,
      } as ColDef,
      {
        field: "id",
        headerName: "Job ID",
        sortable: true,
        filter: true,
      },
      {
        field: "name",
        headerName: "Job Name",
        sortable: true,
        filter: true,
      },
      {
        field: "type",
        headerName: "Type",
        sortable: true,
        filter: true,
      },
      {
        field: "date",
        headerName: "Date",
        sortable: true,
        filter: "agDateColumnFilter",
        filterParams: datefilterParamsYYYYMMDD,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      flex: 1,
      filter: true,
      sortable: true,
    }),
    []
  );

  const handleSelectionChanged = () => {
    const selectedRows = gridApi.current.getSelectedRows();
    setSelectedJobs(selectedRows.map((row: any) => row.id));
    setTouched((prev) => ({ ...prev, jobs: true }));
  };

  const handleAssign = async () => {
    setTouched({ project: true, jobs: true });

    if (!areJobsValid || !isProjectValid) {
      return;
    }

    onAssign(selectedProject.value, selectedJobs);
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <Button
            variant="link"
            className="mb-3 p-0"
            onClick={() => history.goBack()}
          >
            <FontAwesomeIcon icon="arrow-left" className="mr-2" />
            Back
          </Button>
          <h2>Assign Jobs to Project</h2>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Select Project</Form.Label>
            <Select<ProjectOption>
              value={selectedProject}
              onChange={(
                newValue: ProjectOption | null,
                actionMeta: ActionMeta<ProjectOption>
              ) => {
                setSelectedProject(newValue);
                setTouched((prev) => ({ ...prev, project: true }));
              }}
              options={groupedOptions}
              isClearable
              placeholder="Choose a project..."
              className={`basic-select ${showProjectError ? "is-invalid" : ""}`}
              classNamePrefix="select"
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: showProjectError ? "#dc3545" : base.borderColor,
                  "&:hover": {
                    borderColor: showProjectError
                      ? "#dc3545"
                      : base.borderColor,
                  },
                }),
              }}
              formatGroupLabel={(data) => (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>{data.label}</span>
                  <span
                    style={{
                      backgroundColor: "#EBECF0",
                      borderRadius: "2em",
                      color: "#172B4D",
                      display: "inline-block",
                      fontSize: 12,
                      fontWeight: "normal",
                      lineHeight: "1",
                      minWidth: 1,
                      padding: "0.16666666666667em 0.5em",
                      textAlign: "center",
                    }}
                  >
                    {data.options.length}
                  </span>
                </div>
              )}
            />
            {showProjectError && (
              <Form.Control.Feedback
                type="invalid"
                style={{ display: "block" }}
              >
                Please select a project
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Row>
            <span className="ml-4 mr-2">Filter by</span>
            <ExternalFilter
              columnDefs={externalFilterColumns}
              gridApi={gridApi.current}
            />
          </Row>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <div
            className="ag-theme-alpine"
            style={{
              height: "500px",
              width: "100%",
              border: showJobsError ? "1px solid #dc3545" : undefined,
            }}
          >
            <AgGridReact
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              rowModelType="infinite"
              cacheBlockSize={pagination.pageSize}
              cacheOverflowSize={2}
              maxConcurrentDatasourceRequests={1}
              infiniteInitialRowCount={1}
              maxBlocksInCache={10}
              pagination={true}
              paginationPageSize={pagination.pageSize}
              suppressCellFocus={true}
              rowSelection="multiple"
              onSelectionChanged={handleSelectionChanged}
            />
          </div>
          {showJobsError && (
            <div className="text-danger mt-2">
              Please select at least one job
            </div>
          )}
        </Col>
      </Row>

      <Row>
        <Col className="d-flex justify-content-end">
          {(!isProjectValid || !areJobsValid) && (
            <Row className="mt-2">
              <Col className="text-danger text-end">
                <small>
                  {!isProjectValid && !areJobsValid
                    ? "Please select a project and at least one job"
                    : !isProjectValid
                    ? "Please select a project"
                    : "Please select at least one job"}
                </small>
              </Col>
            </Row>
          )}
          <Button
            className="ml-2"
            variant="primary"
            onClick={handleAssign}
            disabled={!isProjectValid || !areJobsValid}
          >
            Assign Jobs
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

export default AssignJobsPage;
